import { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Button,
	CircularProgress,
	Alert,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import supabase from "../../supabaseClient";

// This example assumes a simple local cart state. In production, sync with backend draft order.
const DRAFT_ORDER_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-order-items`;
const CREATE_ORDER_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-order`;

const Cart = () => {
	// In production, fetch draft order and items from backend
	const [cartItems, setCartItems] = useState([]); // [{store_item_id, title, price_cents, quantity}]
	const [orderId, setOrderId] = useState(null); // Should be set after order creation
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const [userProfile, setUserProfile] = useState({
		first_name: "",
		last_name: "",
		email: "",
	});
	const [contact, setContact] = useState({ name: "", email: "", phone: "" });

	useEffect(() => {
		const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");
		// Ensure all cart items have store_item_id
		const normalizedCart = storedCart.map((item) => ({
			...item,
			store_item_id: item.store_item_id || item.id,
		}));
		setCartItems(normalizedCart);
		localStorage.setItem("cart", JSON.stringify(normalizedCart));

		//if no cart items, no need to create order
		if (normalizedCart.length === 0) return;
		// On mount, create or fetch a draft order
		(async () => {
			setLoading(true);
			setError("");
			try {
				const token = process.env.REACT_APP_SUPABASE_ANON_KEY;
				const res = await fetch(CREATE_ORDER_API, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						items: normalizedCart.map((item) => ({
							store_item_id: item.store_item_id,
							quantity: item.quantity,
						})),
					}),
				});
				const data = await res.json();
				if (res.ok && data.order_id) {
					setOrderId(data.order_id);
				} else {
					setError(data.error || "Failed to create order.");
				}
			} catch (err) {
				setError("Failed to create order.");
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		// If user is logged in, fetch profile info
		async function fetchUserProfile() {
			const {
				data: { user },
			} = await supabase.auth.getUser();
			if (user) {
				const { data, error } = await supabase
					.from("profiles")
					.select("first_name, last_name, email")
					.eq("supabaseId", user.id)
					.single();
				if (data && !error) {
					const fullName = [data.first_name, data.last_name]
						.filter(Boolean)
						.join(" ");
					setUserProfile({
						first_name: data.first_name || "",
						last_name: data.last_name || "",
						email: data.email || "",
					});
					setContact({
						name: fullName,
						email: data.email || "",
						phone: "",
					});
				}
			}
		}
		fetchUserProfile();
	}, []);

	const handleContactChange = (e) => {
		const { name, value } = e.target;
		setContact((c) => ({ ...c, [name]: value }));
	};

	const handleQuantityInput = (store_item_id, value) => {
		let quantity = Math.max(1, parseInt(value) || 1);
		if (quantity > 50) {
			quantity = 50;
			setError(
				"For orders over 50 of a single item, please contact Connor at connor@gosellgardens.com for a custom order."
			);
		}
		setCartItems((items) =>
			items.map((item) =>
				item.store_item_id === store_item_id ? { ...item, quantity } : item
			)
		);
		// Persist to localStorage
		const updated = cartItems.map((item) =>
			item.store_item_id === store_item_id ? { ...item, quantity } : item
		);
		localStorage.setItem("cart", JSON.stringify(updated));
	};

	const handleRemove = (store_item_id) => {
		const updated = cartItems.filter(
			(item) => item.store_item_id !== store_item_id
		);
		setCartItems(updated);
		localStorage.setItem("cart", JSON.stringify(updated));
	};

	const handleUpdateOrder = async () => {
		if (!orderId) {
			// If no orderId, create order first
			setLoading(true);
			setError("");
			try {
				const token = process.env.REACT_APP_SUPABASE_ANON_KEY;
				const normalizedCart = cartItems.map((item) => ({
					...item,
					store_item_id: item.store_item_id || item.id,
				}));
				const res = await fetch(CREATE_ORDER_API, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						items: normalizedCart.map((item) => ({
							store_item_id: item.store_item_id,
							quantity: item.quantity,
						})),
					}),
				});
				const data = await res.json();
				if (res.ok && data.order_id) {
					setOrderId(data.order_id);
				} else {
					setError(data.error || "Failed to create order.");
				}
			} catch (err) {
				setError("Failed to create order.");
			}
			setLoading(false);
			return;
		}
		setLoading(true);
		setError("");
		try {
			const token = process.env.REACT_APP_SUPABASE_ANON_KEY;
			const normalizedCart = cartItems.map((item) => ({
				...item,
				store_item_id: item.store_item_id || item.id,
			}));
			const res = await fetch(DRAFT_ORDER_API, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					order_id: orderId,
					items: normalizedCart.map((item) => ({
						store_item_id: item.store_item_id,
						quantity: item.quantity,
					})),
				}),
			});
			const data = await res.json();
			if (!res.ok) setError(data.error || "Failed to update cart.");
		} catch (err) {
			setError("Failed to update cart.");
		}
		setLoading(false);
	};

	const handleCheckout = async () => {
		// Prevent checkout if any contact field is empty
		if (
			!contact.name.trim() ||
			!contact.email.trim() ||
			!contact.phone.trim()
		) {
			setError(
				"Please enter your name, email, and phone number before checking out."
			);
			return;
		}
		setLoading(true);
		setError("");
		try {
			const token = process.env.REACT_APP_SUPABASE_ANON_KEY;

			if (!orderId) {
				setError("No order to checkout. Please update your cart first.");
				setLoading(false);
				return;
			}
			const res = await fetch(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/checkout-order`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						order_id: orderId,
						name: contact.name,
						email: contact.email,
						phone: contact.phone,
					}),
				}
			);
			const data = await res.json();
			if (res.ok && data.checkout_url) {
				window.location.href = data.checkout_url;
			} else {
				setError(data.error || "Failed to start checkout.");
			}
		} catch (err) {
			setError("Failed to start checkout.");
			setLoading(false);
		}
		setTimeout(() => {
			setLoading(false);
		}, 2000);
	};

	const subtotal = cartItems.reduce(
		(sum, item) => sum + item.price_cents * item.quantity,
		0
	);
	const total = subtotal; // Add taxes/shipping if needed

	if (loading)
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<CircularProgress />
			</Box>
		);

	return (
		<Box sx={{ maxWidth: 700, mx: "auto", mt: 4, p: 2 }}>
			<Typography variant="h4" mb={3}>
				Cart
			</Typography>
			{!userProfile && (
				<Paper sx={{ p: 2, mb: 2 }}>
					<Typography variant="h6" mb={1}>
						Contact Information
					</Typography>
					<TextField
						label="Name"
						name="name"
						value={contact.name}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Email"
						name="email"
						value={contact.email}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Phone"
						name="phone"
						value={contact.phone}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
				</Paper>
			)}
			{userProfile && (
				<Paper sx={{ p: 2, mb: 2 }}>
					<Typography variant="h6" mb={1}>
						Contact Information
					</Typography>
					<TextField
						label="Name"
						name="name"
						value={contact.name}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Email"
						name="email"
						value={contact.email}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Phone"
						name="phone"
						value={contact.phone}
						onChange={handleContactChange}
						fullWidth
						margin="normal"
						required
					/>
				</Paper>
			)}
			<Typography variant="h5" mb={2}>
				Items
			</Typography>
			{cartItems.length === 0 ? (
				<Box sx={{ textAlign: "center", mt: 4 }}>
					<Box p={2}>
						<Typography>Your cart is empty.</Typography>
					</Box>
					<Box p={2}>
						<Button variant="contained" href="/store">
							Continue Shopping
						</Button>
					</Box>
				</Box>
			) : (
				cartItems.map((item) => (
					<Paper key={item.store_item_id} sx={{ p: 2, mb: 2 }}>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
						>
							<Typography variant="h6">{item.title}</Typography>
							<Box display="flex" alignItems="center">
								<TextField
									type="number"
									size="small"
									label="Qty"
									value={item.quantity}
									onChange={(e) =>
										handleQuantityInput(item.store_item_id, e.target.value)
									}
									inputProps={{ min: 1, style: { width: 60 } }}
									sx={{ mr: 2 }}
								/>
								<Button
									color="error"
									onClick={() => handleRemove(item.store_item_id)}
								>
									Remove
								</Button>
							</Box>
						</Box>
						<Typography variant="body1" mb={1}>
							{item.description}
						</Typography>
						<Typography variant="subtitle1" mb={1}>
							${(item.price_cents / 100).toFixed(2)}
						</Typography>
					</Paper>
				))
			)}
			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
			{cartItems.length > 0 && (
				<Button variant="contained" sx={{ mt: 2 }} onClick={handleUpdateOrder}>
					Update Cart
				</Button>
			)}
			{cartItems.length > 0 && (
				<Button
					variant="contained"
					color="success"
					sx={{ mt: 2, ml: 2 }}
					onClick={handleCheckout}
				>
					Checkout
				</Button>
			)}
			{cartItems.length > 0 && (
				<Box sx={{ mt: 4, textAlign: "right" }}>
					<Typography variant="subtitle1">
						Subtotal: ${(subtotal / 100).toFixed(2)}
					</Typography>
					<Typography variant="h6" fontWeight="bold">
						Total: ${(total / 100).toFixed(2)}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default Cart;
